import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Leaf, 
  Apple, 
  Droplets, 
  Users, 
  Heart, 
  Globe,
  Shield,
  Award
} from "lucide-react";

const HumanHealthBenefits: React.FC = () => {
  const { t } = useLanguage();

  const healthBenefits = [
    {
      title: t('health.chemical.title'),
      points: [
        t('health.chemical.desc1'),
        t('health.chemical.desc2'),
        t('health.chemical.desc3')
      ],
      icon: Shield,
      color: "from-emerald-400 to-green-600"
    },
    {
      title: t('health.nutrition.title'),
      points: [
        t('health.nutrition.desc1'),
        t('health.nutrition.desc2'),
        t('health.nutrition.desc3')
      ],
      icon: Apple,
      color: "from-amber-400 to-orange-600"
    },
    {
      title: t('health.water.title'),
      points: [
        t('health.water.desc1'),
        t('health.water.desc2'),
        t('health.water.desc3')
      ],
      icon: Droplets,
      color: "from-blue-400 to-cyan-600"
    },
    {
      title: t('health.farmers.title'),
      points: [
        t('health.farmers.desc1'),
        t('health.farmers.desc2'),
        t('health.farmers.desc3')
      ],
      icon: Users,
      color: "from-purple-400 to-indigo-600"
    },
    {
      title: t('health.gut.title'),
      points: [
        t('health.gut.desc1'),
        t('health.gut.desc2'),
        t('health.gut.desc3')
      ],
      icon: Heart,
      color: "from-red-400 to-pink-600"
    },
    {
      title: t('health.environmental.title'),
      points: [
        t('health.environmental.desc1'),
        t('health.environmental.desc2'),
        t('health.environmental.desc3')
      ],
      icon: Globe,
      color: "from-teal-400 to-emerald-600"
    }
  ];

  return (
    <section className="py-24 bg-white dark:bg-emerald-950/20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-6 text-emerald-900 dark:text-white">
            {t('health.title')}
          </h2>
          <p className="text-xl text-emerald-800/70 dark:text-emerald-100/60 max-w-4xl mx-auto leading-relaxed">
            {t('health.subtitle')}
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {healthBenefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            return (
              <div
                key={index}
                className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-white to-emerald-50 dark:from-emerald-950/40 dark:to-emerald-900/20 p-8 border border-emerald-100 dark:border-emerald-800/30 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >
                {/* Background Decoration */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent rounded-full -translate-y-16 translate-x-16 group-hover:scale-150 transition-transform duration-700"></div>
                
                {/* Icon */}
                <div className={`relative z-10 w-16 h-16 mb-6 rounded-xl bg-gradient-to-br ${benefit.color} p-0.5`}>
                  <div className="w-full h-full bg-white dark:bg-emerald-950 rounded-xl flex items-center justify-center">
                    <IconComponent className="w-8 h-8 text-emerald-700 dark:text-emerald-300" />
                  </div>
                </div>

                {/* Title */}
                <h3 className="relative z-10 text-xl font-bold mb-4 text-emerald-900 dark:text-white">
                  {benefit.title}
                </h3>

                {/* Points List */}
                <ul className="relative z-10 space-y-3">
                  {benefit.points.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-emerald-800/70 dark:text-emerald-100/60">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                      <span className="text-sm leading-relaxed">{point}</span>
                    </li>
                  ))}
                </ul>

                {/* Hover Effect */}
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              </div>
            );
          })}
        </div>

        {/* Conclusion Section */}
        <div className="mt-20 p-10 rounded-3xl bg-gradient-to-br from-primary/5 to-emerald-500/5 dark:from-primary/10 dark:to-emerald-500/10 border border-emerald-200 dark:border-emerald-800/30">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="md:w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Award className="w-10 h-10 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-xl md:text-2xl font-medium text-emerald-900 dark:text-white leading-relaxed text-center md:text-left">
                {t('health.conclusion')}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center p-6 rounded-xl bg-emerald-50 dark:bg-emerald-950/40">
            <div className="text-3xl font-bold text-primary mb-2">100%</div>
            <div className="text-sm text-emerald-700 dark:text-emerald-300">Chemical-Free</div>
          </div>
          <div className="text-center p-6 rounded-xl bg-emerald-50 dark:bg-emerald-950/40">
            <div className="text-3xl font-bold text-primary mb-2">40%</div>
            <div className="text-sm text-emerald-700 dark:text-emerald-300">More Nutrients</div>
          </div>
          <div className="text-center p-6 rounded-xl bg-emerald-50 dark:bg-emerald-950/40">
            <div className="text-3xl font-bold text-primary mb-2">0</div>
            <div className="text-sm text-emerald-700 dark:text-emerald-300">Toxic Residues</div>
          </div>
          <div className="text-center p-6 rounded-xl bg-emerald-50 dark:bg-emerald-950/40">
            <div className="text-3xl font-bold text-primary mb-2">100%</div>
            <div className="text-sm text-emerald-700 dark:text-emerald-300">Safe for Farmers</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HumanHealthBenefits;